import { Package, Plus, ShoppingBag } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";

import { listListings, publishListing, type MarketplaceListing } from "@/services/marketplace";

export function MarketplacePage() {
  const [listings, setListings] = React.useState<MarketplaceListing[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [form, setForm] = React.useState({
    type: "app" as MarketplaceListing["type"],
    name: "",
    description: "",
    price: "0",
    currency: "CNY",
    payload: "{}",
  });

  const load = async () => {
    setLoading(true);
    try {
      const data = await listListings();
      setListings(data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    void load();
  }, []);

  const handlePublish = async () => {
    try {
      const payload = JSON.parse(form.payload || "{}") as Record<string, unknown>;
      await publishListing({
        type: form.type,
        name: form.name,
        description: form.description,
        price: Number(form.price),
        currency: form.currency,
        payload,
      });
      setForm({ type: "app", name: "", description: "", price: "0", currency: "CNY", payload: "{}" });
      await load();
    } catch (err) {
      alert(`发布失败：${String(err)}`);
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">交易市场</h1>
          <p className="text-sm text-muted-foreground">浏览、发布和购买应用、组件与模板。</p>
        </div>
        <Dialog>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="mr-1.5 h-4 w-4" />
              发布
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>发布到市场</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="space-y-1">
                <Label className="text-xs">类型</Label>
                <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v as MarketplaceListing["type"] }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="app">应用</SelectItem>
                    <SelectItem value="component">组件</SelectItem>
                    <SelectItem value="template">模板</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">名称</Label>
                <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">描述</Label>
                <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={3} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">价格</Label>
                  <Input type="number" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">货币</Label>
                  <Input value={form.currency} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))} />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Payload (JSON)</Label>
                <Textarea value={form.payload} onChange={(e) => setForm((f) => ({ ...f, payload: e.target.value }))} rows={4} className="font-mono text-xs" />
              </div>
              <Button className="w-full" onClick={() => void handlePublish()}>
                确认发布
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all">全部</TabsTrigger>
          <TabsTrigger value="app">应用</TabsTrigger>
          <TabsTrigger value="component">组件</TabsTrigger>
          <TabsTrigger value="template">模板</TabsTrigger>
        </TabsList>
        {(["all", "app", "component", "template"] as const).map((tab) => (
          <TabsContent key={tab} value={tab} className="data-[state=inactive]:hidden">
            {loading ? (
              <p className="text-sm text-muted-foreground">加载中...</p>
            ) : listings.filter((l) => tab === "all" || l.type === tab).length === 0 ? (
              <p className="text-sm text-muted-foreground">暂无商品</p>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {listings
                  .filter((l) => tab === "all" || l.type === tab)
                  .map((listing) => (
                    <Card key={listing.id}>
                      <CardHeader className="pb-3">
                        <div className="flex items-center gap-2">
                          {listing.type === "app" ? <Package className="h-4 w-4" /> : <ShoppingBag className="h-4 w-4" />}
                          <CardTitle className="text-base">{listing.name}</CardTitle>
                        </div>
                        <CardDescription className="text-xs line-clamp-2">{listing.description || "暂无描述"}</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <div className="text-sm font-medium">
                          {listing.price > 0 ? `¥${listing.price} ${listing.currency}` : "免费"}
                        </div>
                        <div className="text-xs text-muted-foreground">类型：{listing.type}</div>
                        <Button size="sm" variant="outline" className="w-full">
                          查看详情
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
